/**
 * A page address that is safe to send to analytics or error reports.
 *
 * Sign-in returns to the app with its keys in the address: Google and the
 * email links land on `/#access_token=…&refresh_token=…&provider_token=…`.
 * PostHog's page view fires on that address before the auth client tidies it,
 * so 55 page views from 46 people (2026-07-10 → 2026-10-08) carried live
 * sign-in keys into PostHog (founder test, 2026-10-08). The Pilot hand-off
 * link also carries the typed problem in its #fragment (inAppBrowser.ts).
 *
 * So: a #fragment that holds key=value pairs is dropped whole (plain anchors
 * like #pricing stay), and the query keys below are removed.
 *
 * `ph_did` (the PostHog id a hand-off link carries, lib/posthog.ts) isn't
 * secret, but it's PostHog's own id echoed back into its URLs — removed so
 * page addresses stay clean and group together.
 */
const SECRET_QUERY_KEYS = [
  "access_token", "refresh_token", "provider_token", "provider_refresh_token",
  "token", "token_hash", "code",
  "pilot_problem", "pilot_step", "pilot_title",
  "ph_did",
];

export function safeUrl(href: string): string;
export function safeUrl(href: string | null | undefined): string | null | undefined;
export function safeUrl(href: string | null | undefined): string | null | undefined {
  if (!href || typeof href !== "string") return href;
  try {
    const url = new URL(href);
    if (url.hash.includes("=")) url.hash = "";
    for (const k of SECRET_QUERY_KEYS) url.searchParams.delete(k);
    return url.toString();
  } catch {
    // Not a full URL (a bare path): cut anything that could hold a key.
    return href.replace(/#.*=.*$/, "").replace(/([?&])(access_token|refresh_token|provider_token|token|code)=[^&#]*/g, "$1");
  }
}

/** Does this address hold anything safeUrl would remove? Used where the
 *  address can't be cleaned first — the Meta Pixel reads it itself. */
export function hasUrlSecrets(href: string): boolean {
  try {
    const url = new URL(href);
    return url.hash.includes("=") || SECRET_QUERY_KEYS.some((k) => url.searchParams.has(k));
  } catch {
    return true; // can't tell → treat as unsafe
  }
}

/** URL-valued properties analytics SDKs attach to events and people. */
const URL_PROPS = ["$current_url", "$referrer", "$initial_current_url", "$initial_referrer", "$session_entry_url"];

/** Cleans the URL properties of an event's property bag in place. */
export function scrubUrlProps(props: Record<string, unknown> | null | undefined): void {
  if (!props) return;
  for (const k of URL_PROPS) {
    const v = props[k];
    if (typeof v === "string") props[k] = safeUrl(v);
  }
}
